import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { combineLatest } from 'rxjs';
import {

    distinctUntilChanged, filter, first, map, skip, switchMap, take,
} from 'rxjs/operators';

import { filterDuplicatedProductsArray, filterEmptyProductsArray } from '../../application/services/productsArray.service';
import { getProductsArrayContextUseCase, getProductsArrayWithoutCategoryUseCase$ } from '../../application/use-cases/productsArray.use-cases';
import { MAX_PRODUCT_LIST } from '../../domain/constants/productsArray.constants';
import { ProductsArrayApiRepository } from '../api/productsArray-api.repository';
import { ProductsArrayQueriesFacade } from '../facades/queries/productsArray.queries.facades';
import { ProductsArraySharedQueriesFacade } from '../facades/queries/shared-queries.facades';

import {
    ClearProductsArray,
    LoadProductsArray,
    LoadProductsArrayWithoutCategory,
} from './productsArray-commands.actions';
import { LoadProductsArraySuccess, LoadProductsArrayWithoutCategorySuccess } from './productsArray-events.actions';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';

/**
 * Efectos base
 */
@Injectable()
export class ProductsArrayEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly productsArrayQueriesFacade: ProductsArrayQueriesFacade,
        private readonly productsArraySharedQueriesFacade: ProductsArraySharedQueriesFacade,
        private readonly productsArrayApiRepository: ProductsArrayApiRepository,
        private readonly platform: CorePlatformService,
    ) { }

    public loadProductsArray$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsArray),
            filter(({ productsArray }) => filterEmptyProductsArray(productsArray)),
            concatLatestFrom(() => this.productsArrayQueriesFacade.getProductsList$()),
            map(([action, productsList]) => {
                return filterDuplicatedProductsArray(action.productsArray, productsList);
            }),
            map((productsArray) => LoadProductsArraySuccess({ productsArray }))
        );
    });

    // Limpiamos la store una vez sobrepasemos el límite establecido de productos almacenados y cambiemos de url
    public onPageChangeClearProductsArray$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsArray),
            concatLatestFrom(() => this.productsArrayQueriesFacade.getProductsList$()),
            filter(([_, productsList]) => Object.entries(productsList).length > MAX_PRODUCT_LIST),
            switchMap(() => this.productsArraySharedQueriesFacade.getUrlFromRouter$().pipe(
                skip(1), // Skip the first value and only emit when the value changes
                distinctUntilChanged(),
                take(1)
            )),
            map(() => ClearProductsArray())
        );
    });

    public loadProductsArrayWithoutCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsArrayWithoutCategory),
            switchMap((action) => {
                return combineLatest([
                    this.productsArraySharedQueriesFacade.getStoreAndLanguage$(),
                    this.productsArraySharedQueriesFacade.getCatalogId$(),
                    this.productsArraySharedQueriesFacade.getContext$().pipe(
                        map((context) => getProductsArrayContextUseCase(context))
                    ),
                ]).pipe(
                    // eslint-disable-next-line max-len
                    filter(([storeAndLanguage, catalogId, productsArrayContext]) => !!storeAndLanguage && !!catalogId && !!productsArrayContext),
                    first(),
                    switchMap(([storeAndLanguage, catalogId, productsArrayContext]) => {
                        return getProductsArrayWithoutCategoryUseCase$(
                            this.productsArrayApiRepository,
                            storeAndLanguage.storeId,
                            catalogId,
                            storeAndLanguage.languageId,
                            action.productsId,
                            productsArrayContext,
                            this.platform.isBrowser(),
                        ).pipe(
                            map((products) => LoadProductsArrayWithoutCategorySuccess({ products, payload: action.payload }))
                        );
                    })
                );
            }),
        );
    });
}
